"use strict";
/*
 * Copyright 2024, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.ParamsValidatorUtils = void 0;
const uuid_1 = require("uuid");
const geojson_validation_1 = require("geojson-validation");
class ParamsValidatorUtils {
    // isRecord checks if the value is a plain object (Record type) - excludes arrays, primitives, and class instances
    static isRecord(attribute, value, displayName, allowNullParameter = true) {
        // undefined is always allowed
        if (value === undefined) {
            return [];
        }
        // null is conditionally allowed based on allowNullParameter
        if (value === null) {
            if (allowNullParameter) {
                return [];
            }
            else {
                return [new Error(`${displayName} validateParams: ${attribute} should not be null`)];
            }
        }
        // Check if it's a plain object (Record type)
        if (typeof value !== 'object' || Array.isArray(value) || value.constructor !== Object) {
            return [new Error(`${displayName} validateParams: ${attribute} should be a plain object (Record)`)];
        }
        return [];
    }
    static isRequired(attribute, value, displayName) {
        if (value === undefined || value === null) {
            return [new Error(`${displayName} validateParams: ${attribute} is required`)];
        }
        else {
            return [];
        }
    }
    static isInstanceOf(attribute, value, displayName, _class) {
        if (value !== undefined && value !== null && !(value instanceof _class)) {
            return [new Error(`${displayName} validateParams: ${attribute} should be an instance of ${_class.name}`)];
        }
        else {
            return [];
        }
    }
    static isString(attribute, value, displayName) {
        if (value !== undefined && value !== null && typeof value !== 'string') {
            return [new Error(`${displayName} validateParams: ${attribute} should be a string`)];
        }
        else {
            return [];
        }
    }
    static isNonEmptyString(attribute, value, displayName) {
        if (value !== undefined && value !== null && (typeof value !== 'string' || value.trimStart().trim() === '')) {
            return [new Error(`${displayName} validateParams: ${attribute} should be a non-empty string`)];
        }
        else {
            return [];
        }
    }
    static isBoolean(attribute, value, displayName) {
        if (value !== undefined && value !== null && typeof value !== 'boolean') {
            return [new Error(`${displayName} validateParams: ${attribute} should be a boolean`)];
        }
        else {
            return [];
        }
    }
    static isUuid(attribute, value, displayName) {
        if (value !== undefined && value !== null && (typeof value !== 'string' || !(0, uuid_1.validate)(value))) {
            return [new Error(`${displayName} validateParams: ${attribute} should be a valid uuid`)];
        }
        else {
            return [];
        }
    }
    static isPositiveInteger(attribute, value, displayName) {
        if (value !== undefined && value !== null && (!Number.isInteger(value) || Number(value) < 0)) {
            return [new Error(`${displayName} validateParams: ${attribute} should be a positive integer`)];
        }
        else {
            return [];
        }
    }
    static isPositiveNumber(attribute, value, displayName) {
        if ((value !== undefined && value !== null && typeof value !== 'number') || Number(value) < 0) {
            return [new Error(`${displayName} validateParams: ${attribute} should be a positive number`)];
        }
        else {
            return [];
        }
    }
    // a date string is a string of format 'YYYY-MM-DD'
    static isDateString(attribute, value, displayName) {
        if (value !== undefined && value !== null) {
            const date = new Date(value + 'T00:00:00');
            if (!(date instanceof Date) || isNaN(date.getDate())) {
                return [new Error(`${displayName} validateParams: ${attribute} should be a valid date string`)];
            }
        }
        return [];
    }
    static isIn(attribute, value, displayName, inArray, typeName) {
        if (value !== undefined && value !== null && !inArray.includes(value)) {
            return [
                new Error(`${displayName} validateParams: ${attribute} ${typeName ? `should be one of the valid ${typeName} values` : 'is invalid'}`)
            ];
        }
        else {
            return [];
        }
    }
    static isArray(attribute, value, displayName) {
        if (value !== undefined && value !== null && !Array.isArray(value)) {
            return [new Error(`${displayName} validateParams: ${attribute} should be an array`)];
        }
        else {
            return [];
        }
    }
    static isArrayOfStrings(attribute, value, displayName) {
        if (value !== undefined &&
            value !== null &&
            (!Array.isArray(value) || value.some((v) => typeof v !== 'string'))) {
            return [new Error(`${displayName} validateParams: ${attribute} should be an array of strings`)];
        }
        else {
            return [];
        }
    }
    static isArrayOfDateStrings(attribute, value, displayName) {
        if (value !== undefined && value !== null && Array.isArray(value)) {
            for (let i = 0, countI = value.length; i < countI; i++) {
                if (typeof value[i] !== 'string') {
                    return [new Error(`${displayName} validateParams: ${attribute} should be an array of strings`)];
                }
                const date = new Date(value[i] + 'T00:00:00');
                if (!(date instanceof Date) || isNaN(date.getDate())) {
                    return [
                        new Error(`${displayName} validateParams: ${attribute} should be an array of date strings`)
                    ];
                }
            }
        }
        else if (value !== undefined && value !== null && !Array.isArray(value)) {
            return [new Error(`${displayName} validateParams: ${attribute} should be an array`)];
        }
        return [];
    }
    static isArrayOfNumbers(attribute, value, displayName) {
        if (value !== undefined &&
            value !== null &&
            (!Array.isArray(value) || value.some((v) => typeof v !== 'number'))) {
            return [new Error(`${displayName} validateParams: ${attribute} should be an array of numbers`)];
        }
        else {
            return [];
        }
    }
    static isArrayOfUuids(attribute, value, displayName) {
        if (value !== undefined && value !== null && (!Array.isArray(value) || value.some((v) => !(0, uuid_1.validate)(v)))) {
            return [new Error(`${displayName} validateParams: ${attribute} should be an array of valid uuids`)];
        }
        else {
            return [];
        }
    }
    static isArrayOf(attribute, value, displayName, _class) {
        if (value !== undefined &&
            value !== null &&
            (!Array.isArray(value) || value.some((v) => !(v instanceof _class)))) {
            return [new Error(`${displayName} validateParams: ${attribute} should be an array of ${_class.name}`)];
        }
        else {
            return [];
        }
    }
    static isGeojsonPoint(attribute, value, displayName) {
        if (value !== undefined &&
            value !== null &&
            (!(0, geojson_validation_1.isFeature)(value) || !(0, geojson_validation_1.isPoint)(value.geometry))) {
            return [new Error(`${displayName} validateParams: ${attribute} should be a valid geojson point`)];
        }
        else {
            return [];
        }
    }
    static isGeojsonLineString(attribute, value, displayName) {
        if (value !== undefined &&
            value !== null &&
            (!(0, geojson_validation_1.isFeature)(value) || !(0, geojson_validation_1.isLineString)(value.geometry))) {
            return [new Error(`${displayName} validateParams: ${attribute} should be a valid geojson line string`)];
        }
        else {
            return [];
        }
    }
    static isGeojsonPolygon(attribute, value, displayName) {
        if (value !== undefined &&
            value !== null &&
            (!(0, geojson_validation_1.isFeature)(value) || !(0, geojson_validation_1.isPolygon)(value.geometry))) {
            return [new Error(`${displayName} validateParams: ${attribute} should be a valid geojson polygon`)];
        }
        else {
            return [];
        }
    }
    static isGeojsonMultiPoint(attribute, value, displayName) {
        if (value !== undefined &&
            value !== null &&
            (!(0, geojson_validation_1.isFeature)(value) || !(0, geojson_validation_1.isMultiPoint)(value.geometry))) {
            return [new Error(`${displayName} validateParams: ${attribute} should be a valid geojson multi point`)];
        }
        else {
            return [];
        }
    }
    static isGeojsonMultiLineString(attribute, value, displayName) {
        if (value !== undefined &&
            value !== null &&
            (!(0, geojson_validation_1.isFeature)(value) || !(0, geojson_validation_1.isMultiLineString)(value.geometry))) {
            return [
                new Error(`${displayName} validateParams: ${attribute} should be a valid geojson multi line string`)
            ];
        }
        else {
            return [];
        }
    }
    static isGeojsonMultiPolygon(attribute, value, displayName) {
        if (value !== undefined &&
            value !== null &&
            (!(0, geojson_validation_1.isFeature)(value) || !(0, geojson_validation_1.isMultiPolygon)(value.geometry))) {
            return [new Error(`${displayName} validateParams: ${attribute} should be a valid geojson multi polygon`)];
        }
        else {
            return [];
        }
    }
    static isGeojsonFeatureCollection(attribute, value, displayName) {
        if (value !== undefined && value !== null && !(0, geojson_validation_1.isFeatureCollection)(value)) {
            return [
                new Error(`${displayName} validateParams: ${attribute} should be a valid geojson feature collection`)
            ];
        }
        else {
            return [];
        }
    }
}
exports.ParamsValidatorUtils = ParamsValidatorUtils;
//# sourceMappingURL=ParamsValidatorUtils.js.map